import moment from "moment-timezone";
import React, { ReactNode, Ref, useState } from "react";
import { useStore } from "src/stores/Store";
import { dateFormatMed } from "src/utils/constants";
import tw from "src/utils/tw";
import { Day } from "types/code-generator";
import { BorderButton } from "../buttons/BorderButton";
import Modal from "../modals/Modal";
import InputCalendar from "./InputCalendar";
import { InputText } from "./InputText";

interface Props {
  onChange?: (date: Date, endDate?: Date) => void;
  value?: Date | "";
  endValue?: Date | "";
  placeholder?: string;
  calendarOnly?: boolean;
  visible?: boolean;
  handleCancel?: () => void;
  innerRef?: Ref<any>;
  format?: string;
  allowClear?: boolean;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  validDays?: Day[];
  hideIcon?: boolean;
  range?: boolean;
  suffix?: ReactNode;
  utc?: boolean;
}

function InputDate(props: Props) {
  const store = useStore();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(
    props.value
      ? moment.isMoment(props.value)
        ? moment(props.value.toDate())
        : moment(props.value)
      : moment()
  );
  const [firstDate, setFirstDate] = useState(null);

  function onChange(date: Date) {
    setValue(moment(date));
  }

  function nextMonth() {
    const newDate = moment(value || new Date()).add(1, "month");
    setValue(newDate);
  }

  function prevMonth() {
    const newDate = moment(value || new Date()).subtract(1, "month");
    setValue(newDate);
  }

  function setToday() {
    const now = moment();
    const minDate = props.minDate && moment(props.minDate);
    const maxDate = props.maxDate && moment(props.maxDate);

    if (
      (minDate && maxDate && now.isBetween(minDate, maxDate)) ||
      (minDate && now.isAfter(minDate)) ||
      (maxDate && now.isBefore(maxDate)) ||
      (!minDate && !maxDate)
    ) {
      props.onChange(now.toDate());
      setValue(now);
      setOpen(false);
    }

    // setValue(moment(today));
  }

  function onSelectDate(date: Date) {
    if (!date) {
      props.onChange(null);
      return;
    }

    const newDate = moment(date);

    if (props.range) {
      if (firstDate) {
        props.onChange(firstDate.toDate(), date);
        setFirstDate(null);
        setOpen(false);
      } else {
        setFirstDate(newDate);
      }
    } else {
      setOpen(false);
      setValue(newDate);

      props.onChange(date);
    }
  }

  function handleCancel() {
    if (props.handleCancel) {
      props.handleCancel();
    } else {
      setOpen(false);
    }
  }

  let display = "";

  if (props.value) {
    display = moment(props.value).format(
      props.format ? props.format : dateFormatMed
    );

    if (props.utc) {
      display = moment
        .utc(props.value)
        .format(props.format ? props.format : dateFormatMed);
    }

    if (props.range && props.endValue) {
      display = `${moment(props.value).format(
        props.format ? props.format : dateFormatMed
      )}–${moment(props.endValue).format(
        props.format ? props.format : dateFormatMed
      )}`;
    }
  }

  let suffix;

  if (props.allowClear && props.value) {
    suffix = (
      <button
        className="mr-4 mt-3 cursor-pointer"
        onClick={() => onSelectDate(null)}
      >
        <i aria-hidden className="far fa-circle-xmark text-empty" />
      </button>
    );
  }

  if (props.suffix) {
    suffix = props.suffix;
  }

  return (
    <>
      {!props.calendarOnly && (
        <div className="flex items-center">
          <div className="w-full flex-1 cursor-pointer">
            <InputText
              prefix={
                !props.hideIcon && (
                  <i
                    className="far fa-calendar"
                    css={{
                      color: store.theme.orgColor,
                    }}
                    onClick={() => setOpen(true)}
                  />
                )
              }
              value={display}
              name="date"
              placeholder={props.placeholder}
              className={tw("cursor-pointer", props.className)}
              readOnly
              onClick={() => setOpen(true)}
              ref={props.innerRef}
              disabled={props.disabled}
              suffix={suffix}
            />
          </div>
        </div>
      )}

      <Modal
        visible={open === true || props.visible === true}
        title={
          props.range
            ? firstDate
              ? "Select Second Date"
              : "Select First Date"
            : "Select Date"
        }
        footer={
          <>
            <BorderButton onClick={prevMonth} className="w-full flex-1">
              Prev<span className="sm:hidden"> Month</span>
            </BorderButton>
            <BorderButton onClick={setToday} className="w-full flex-1">
              Today
            </BorderButton>
            <BorderButton onClick={nextMonth} className="w-full flex-1">
              Next<span className="sm:hidden"> Month</span>
            </BorderButton>
          </>
        }
        handleCancel={handleCancel}
      >
        <InputCalendar
          initialValue={props.range && firstDate ? firstDate : props.value}
          value={value ? value.toDate() : new Date()}
          endValue={!firstDate && props.endValue}
          range={props.range}
          onSelectDate={onSelectDate}
          onChange={onChange}
          minDate={
            props.range && firstDate ? firstDate.toDate() : props.minDate
          }
          maxDate={props.maxDate}
          validDays={props.validDays}
          utc={props.utc}
        />
      </Modal>
    </>
  );
}

export default React.forwardRef((props: Props, ref) => (
  <InputDate {...props} innerRef={ref} />
));
